import type React from 'react';
import { addMinutes, subMinutes } from 'date-fns';
import type { ProjectSummary } from '../../graphql/projectSummary';
import { DEVICE_STATUS_DOT_CLASSES, StatusDot } from '../StatusDot/StatusDot';
import { getUsageSeverityClasses, UsageProgressBar } from '../UsageProgressBar/UsageProgressBar';
import { format } from '../../../../../utils/formatting';
import classNames from '../../../../../utils/classNames';

const QUICK_VIEW_PANEL_CLASSES = 'flex min-w-56 flex-col gap-2';
const QUICK_VIEW_TITLE_CLASSES = 'text-sm font-semibold';
const QUICK_VIEW_HINT_CLASSES = 'text-xs text-gray-500';
const QUICK_VIEW_LABEL_CLASSES = 'truncate text-xs font-medium';
const QUICK_VIEW_META_CLASSES = 'shrink-0 text-xs text-gray-600';
const QUICK_VIEW_ITEM_CLASSES =
    'grid grid-cols-[minmax(0,1fr)_auto] items-center rounded border border-gray-200';

type DataWarningDevice = Pick<
    ProjectSummary['dataWarnings']['devices'][number],
    'measuringPointId' | 'deviceName' | 'usagePercentage' | 'isLimit'
>;
type RecentExceedanceEvent = Pick<
    ProjectSummary['recentExceedances']['events'][number],
    'id' | 'measuringPointId' | 'deviceName' | 'occurredAt'
>;

interface SummaryQuickViewListProps<Item> {
    items: Item[];
    title: string;
    description: string;
    emptyStateLabel: string;
    getKey: (item: Item) => string;
    renderItem: (item: Item) => React.ReactNode;
    itemClassName?: string;
}

function SummaryQuickViewList<Item>({
    items,
    title,
    description,
    emptyStateLabel,
    getKey,
    renderItem,
    itemClassName = classNames(QUICK_VIEW_ITEM_CLASSES, 'gap-2 px-2 py-1'),
}: SummaryQuickViewListProps<Item>) {
    return (
        <div className={QUICK_VIEW_PANEL_CLASSES}>
            <div className="flex flex-col gap-1">
                <p className={QUICK_VIEW_TITLE_CLASSES}>{title}</p>
                <p className={QUICK_VIEW_HINT_CLASSES}>{description}</p>
            </div>
            {items.length ? (
                <ul className="max-h-64 space-y-1 overflow-y-auto pr-1">
                    {items.map((item) => (
                        <li key={getKey(item)} className={itemClassName}>
                            {renderItem(item)}
                        </li>
                    ))}
                </ul>
            ) : (
                <p className={QUICK_VIEW_HINT_CLASSES}>{emptyStateLabel}</p>
            )}
        </div>
    );
}

function getDataWarningLine(deviceName: string, percent: string): string {
    return interpolate(gettext('DEVICE_DATA_USED_PERCENT'), { device: deviceName, percent }, true);
}

function getDeviceStatusLine(count: number, total: number, status: string): string {
    const message = ngettext('DEVICE_STATUS_LINE_SINGULAR', 'DEVICE_STATUS_LINE_PLURAL', count);
    return interpolate(message, { count, total, status: status.toLowerCase() }, true);
}

function formatTimestamp(timestamp: string): string {
    return format(new Date(timestamp), { useBrowserTimezone: true, hideSeconds: true });
}

export function DataWarningsQuickView({ devices }: { devices: DataWarningDevice[] }) {
    return (
        <SummaryQuickViewList
            items={devices}
            title={gettext('DATA_WARNINGS')}
            description={gettext('DATA_WARNINGS_DESCRIPTION')}
            emptyStateLabel={gettext('NO_DATA_WARNING_DEVICES')}
            getKey={(device) => device.measuringPointId}
            itemClassName={classNames(QUICK_VIEW_ITEM_CLASSES, 'gap-3 px-2 py-1.5')}
            renderItem={(device) => {
                const percentLabel = device.usagePercentage.toFixed(0);

                return (
                    <>
                        <span className={classNames(QUICK_VIEW_LABEL_CLASSES, 'text-gray-700')}>
                            {device.deviceName}
                        </span>
                        <div className="flex items-center gap-1">
                            <UsageProgressBar
                                percent={device.usagePercentage}
                                ariaLabel={getDataWarningLine(device.deviceName, percentLabel)}
                                fillClassName={getUsageSeverityClasses(device.isLimit).bar}
                            />
                            <span className="w-8 text-right text-xs text-gray-600">
                                {percentLabel}%
                            </span>
                        </div>
                    </>
                );
            }}
        />
    );
}

function buildSbrViewUrl(measuringPointId: string, occurredAt: string): string {
    const occurredAtDate = new Date(occurredAt);
    const start = subMinutes(occurredAtDate, 5).getTime();
    const end = addMinutes(occurredAtDate, 5).getTime();
    return `/sbr/sbrView/?start=${start}&end=${end}#${measuringPointId}`;
}

export function RecentExceedancesQuickView({ events }: { events: RecentExceedanceEvent[] }) {
    return (
        <SummaryQuickViewList
            items={events}
            title={gettext('RECENT_EXCEEDANCES')}
            description={gettext('RECENT_EXCEEDANCES_DESCRIPTION')}
            emptyStateLabel={gettext('NO_RECENT_EXCEEDANCES')}
            getKey={(event) => event.id}
            renderItem={(event) => (
                <>
                    <a
                        className={classNames(QUICK_VIEW_LABEL_CLASSES, 'hover:underline')}
                        href={buildSbrViewUrl(event.measuringPointId, event.occurredAt)}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {event.deviceName}
                    </a>
                    <time className={QUICK_VIEW_META_CLASSES} dateTime={event.occurredAt}>
                        {formatTimestamp(event.occurredAt)}
                    </time>
                </>
            )}
        />
    );
}

interface DeviceStatusQuickViewProps {
    online: number;
    offline: number;
    inactive: number;
    total: number;
}

function DeviceStatusSummaryRow({ line, dotClassName }: { line: string; dotClassName: string }) {
    return (
        <li className="flex items-center gap-2 rounded border border-gray-200 px-2 py-1.5">
            <StatusDot colorClassName={dotClassName} />
            <span className="text-xs font-medium text-gray-700">{line}</span>
        </li>
    );
}

export function DeviceStatusQuickView({
    online,
    offline,
    inactive,
    total,
}: DeviceStatusQuickViewProps) {
    return (
        <div className={QUICK_VIEW_PANEL_CLASSES}>
            <p className={QUICK_VIEW_TITLE_CLASSES}>{gettext('STATUS')}</p>
            <ul className="space-y-1">
                <DeviceStatusSummaryRow
                    line={getDeviceStatusLine(online, total, gettext('ONLINE'))}
                    dotClassName={DEVICE_STATUS_DOT_CLASSES.online}
                />
                <DeviceStatusSummaryRow
                    line={getDeviceStatusLine(offline, total, gettext('OFFLINE'))}
                    dotClassName={DEVICE_STATUS_DOT_CLASSES.offline}
                />
                <DeviceStatusSummaryRow
                    line={getDeviceStatusLine(inactive, total, gettext('INACTIVE'))}
                    dotClassName={DEVICE_STATUS_DOT_CLASSES.inactive}
                />
            </ul>
        </div>
    );
}
